import type { CoachTopicId } from '../coach/knowledge/topics';
import { compareSuppliersMeta } from './compare-suppliers.meta';
import { executeClientActionsMeta } from './execute-client-actions.meta';
import { getBudgetBreakdownMeta } from './get-budget-breakdown.meta';
import { findChecklistTasksMeta } from './find-checklist-tasks.meta';
import { findGuestsMeta } from './find-guests.meta';
import { getChecklistTasksMeta } from './get-checklist-tasks.meta';
import { getCoachReferenceMeta } from './get-coach-reference.meta';
import { getSupplierDetailsMeta } from './get-supplier-details.meta';
import { planningSnapshotMeta } from './planning-snapshot.meta';
import { readCoachArticleMeta } from './read-coach-article.meta';
import { searchSupplierByNameMeta } from './search-supplier-by-name.meta';
import { searchWeddingSuppliersMeta } from './search-suppliers.meta';
import { searchWeddingKnowledgeMeta } from './search-wedding-knowledge.meta';

export type ToolMeta = {
  readonly description: string;
  readonly examples: readonly string[];
  // What it does for the couple, in one clause the coach can say.
  readonly value: string;
  // The reference to load before a chip turn that names this capability.
  readonly grounding?: CoachTopicId;
  // The most demanding arguments, so every readiness check tests the same input. Absent
  // means it is checked with no arguments.
  readonly strictestArgs?: unknown;
};

/**
 * The tools the agent can call, and the example prompts the debug panel shows.
 * Metadata comes from type-only `.meta.ts` files, so the client can import this without
 * pulling server code into a page bundle. To add a tool: add its `.meta.ts`, an entry here,
 * and a row in the chat's tool-card table (a card or null). The build fails without it.
 */
export const TOOL_REGISTRY = [
  { name: 'getChecklistTasks', meta: getChecklistTasksMeta },
  { name: 'searchWeddingKnowledge', meta: searchWeddingKnowledgeMeta },
  { name: 'searchWeddingSuppliers', meta: searchWeddingSuppliersMeta },
  { name: 'searchSupplierByName', meta: searchSupplierByNameMeta },
  { name: 'getSupplierDetails', meta: getSupplierDetailsMeta },
  { name: 'compareSuppliers', meta: compareSuppliersMeta },
  { name: 'planningSnapshot', meta: planningSnapshotMeta },
  { name: 'getBudgetBreakdown', meta: getBudgetBreakdownMeta },
  { name: 'findChecklistTasks', meta: findChecklistTasksMeta },
  { name: 'findGuests', meta: findGuestsMeta },
  { name: 'getCoachReference', meta: getCoachReferenceMeta },
  { name: 'readCoachArticle', meta: readCoachArticleMeta },
  { name: 'executeClientActions', meta: executeClientActionsMeta },
] as const;

export type ToolRegistryEntry = (typeof TOOL_REGISTRY)[number];
export type ToolName = ToolRegistryEntry['name'];

// A reference to a tool: its registry entry, or any object with its name.
export type ToolRef<NAME extends ToolName = ToolName> = { readonly name: NAME };

// The registry by name, so code refers to a tool as `TOOLS.planningSnapshot`, not a string.
// Removing a tool breaks every reference to it.
export const TOOLS = Object.fromEntries(TOOL_REGISTRY.map((entry) => [entry.name, entry])) as {
  [ENTRY in ToolRegistryEntry as ENTRY['name']]: ENTRY;
};

// What each tool does for the couple, from its meta. Every prompt that names a tool reads
// this, so they all describe it the same way.
export const CAPABILITY_VALUE = Object.fromEntries(
  TOOL_REGISTRY.map((entry) => [entry.name, entry.meta.value]),
) as Record<ToolName, string>;

export const ACTIVE_TOOL_NAMES: ToolName[] = TOOL_REGISTRY.map((t) => t.name);
